import { useState, useEffect } from 'react';
import { inventoryAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Loader2,
  Plus,
  Search,
  Package,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  Pencil,
  Trash2,
  ArrowUpCircle,
  ArrowDownCircle,
  Warehouse
} from 'lucide-react';

export default function InventoryManagement() {
  const [items, setItems] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedWarehouse, setSelectedWarehouse] = useState('all');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showTransactionDialog, setShowTransactionDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  
  const [itemFormData, setItemFormData] = useState({
    sku: '',
    name: '',
    description: '',
    category: '',
    quantity: 0,
    unit_price: 0,
    reorder_point: 10,
    warehouse: 'Main Warehouse',
    unit_of_measurement: 'pcs',
    tracking_type: 'none',
    bin_location: '',
    is_composite: false,
    composite_items: []
  });

  const [transactionFormData, setTransactionFormData] = useState({
    item_id: '',
    transaction_type: 'in',
    quantity: 0,
    reference: '',
    notes: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [itemsRes, categoriesRes, warehousesRes, transactionsRes] = await Promise.all([
        inventoryAPI.listItems(),
        inventoryAPI.getCategories(),
        inventoryAPI.getWarehouses(),
        inventoryAPI.listTransactions({ limit: 50 })
      ]);
      
      setItems(itemsRes.data || []);
      setCategories(categoriesRes.data?.categories || []);
      setWarehouses(warehousesRes.data?.warehouses || []);
      setTransactions(transactionsRes.data || []);
    } catch (err) {
      console.error('Failed to load inventory data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateItem = async (e) => {
    e.preventDefault();
    try {
      await inventoryAPI.createItem(itemFormData);
      setShowAddDialog(false);
      setItemFormData({
        sku: '',
        name: '',
        description: '',
        category: '',
        quantity: 0,
        unit_price: 0,
        reorder_point: 10,
        warehouse: 'Main Warehouse',
        unit_of_measurement: 'pcs',
        tracking_type: 'none',
        bin_location: '',
        is_composite: false,
        composite_items: []
      });
      await loadData();
    } catch (err) {
      console.error('Failed to create item:', err);
      alert(err.response?.data?.detail || 'Failed to create item');
    }
  };

  const handleCreateTransaction = async (e) => {
    e.preventDefault();
    try {
      await inventoryAPI.createTransaction(transactionFormData);
      setShowTransactionDialog(false);
      setTransactionFormData({
        item_id: '',
        transaction_type: 'in',
        quantity: 0,
        reference: '',
        notes: ''
      });
      await loadData();
    } catch (err) {
      console.error('Failed to create transaction:', err);
      alert(err.response?.data?.detail || 'Failed to create transaction');
    }
  };

  const handleDeleteItem = async (itemId, itemName) => {
    if (!confirm(`Delete ${itemName}? This action cannot be undone.`)) return;
    
    try {
      await inventoryAPI.deleteItem(itemId);
      await loadData();
    } catch (err) {
      console.error('Failed to delete item:', err);
      alert('Failed to delete item');
    }
  };

  const filteredItems = items.filter(item => {
    const matchesSearch = searchQuery === '' ||
      item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.sku.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
    const matchesWarehouse = selectedWarehouse === 'all' || item.warehouse === selectedWarehouse;
    const matchesTab = activeTab === 'all' || 
      (activeTab === 'low-stock' && item.is_low_stock) ||
      (activeTab === 'in-stock' && !item.is_low_stock);
    
    return matchesSearch && matchesCategory && matchesWarehouse && matchesTab;
  });

  const totalValue = items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
  const lowStockCount = items.filter(item => item.is_low_stock).length;

  return (
    <div className="flex-1 p-4 md:p-6 lg:p-8 overflow-auto">
      <div className="mb-4 md:mb-6">
        <h1 className="text-xl md:text-2xl font-bold mb-2 text-slate-800">Inventory Management</h1>
        <p className="text-sm md:text-base text-slate-500">Track stock levels, items, and transactions</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Card className="bg-white border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Items</p>
                <p className="text-2xl font-bold text-slate-800">{items.length}</p>
              </div>
              <Package className="w-10 h-10 text-cyan-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Low Stock</p>
                <p className="text-2xl font-bold text-orange-600">{lowStockCount}</p>
              </div>
              <AlertTriangle className="w-10 h-10 text-orange-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Value</p>
                <p className="text-2xl font-bold text-green-600">${totalValue.toFixed(2)}</p>
              </div>
              <TrendingUp className="w-10 h-10 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Warehouses</p>
                <p className="text-2xl font-bold text-slate-800">{warehouses.length}</p>
              </div>
              <Warehouse className="w-10 h-10 text-slate-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Actions Bar */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search by name or SKU..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-white border-slate-200 h-11 md:h-10"
          />
        </div>

        <Select value={selectedCategory} onValueChange={setSelectedCategory}>
          <SelectTrigger className="w-full sm:w-48 bg-white h-11 md:h-10">
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {categories.map(cat => (
              <SelectItem key={cat} value={cat}>{cat}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={selectedWarehouse} onValueChange={setSelectedWarehouse}>
          <SelectTrigger className="w-full sm:w-48 bg-white h-11 md:h-10">
            <SelectValue placeholder="Warehouse" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Warehouses</SelectItem>
            {warehouses.map(wh => (
              <SelectItem key={wh} value={wh}>{wh}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Button
          onClick={() => setShowAddDialog(true)}
          className="bg-cyan-500 hover:bg-cyan-600 rounded-full min-h-[44px] md:min-h-10"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Item
        </Button>

        <Button
          onClick={() => setShowTransactionDialog(true)}
          variant="outline"
          className="rounded-full min-h-[44px] md:min-h-10"
        >
          <ArrowUpCircle className="w-4 h-4 mr-2" />
          Stock In/Out
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="all">All Items ({items.length})</TabsTrigger>
          <TabsTrigger value="low-stock">Low Stock ({lowStockCount})</TabsTrigger>
          <TabsTrigger value="in-stock">In Stock ({items.length - lowStockCount})</TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab}>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              {/* Desktop Table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">SKU / Barcode</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Name</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Category</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Quantity</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Price</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Location</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Tracking</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {filteredItems.map((item) => (
                      <tr key={item.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3">
                          <div>
                            <span className="text-sm font-mono text-slate-600 block">{item.sku}</span>
                            {item.barcode && (
                              <span className="text-xs text-slate-400 font-mono">{item.barcode}</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="text-sm font-medium text-slate-800">{item.name}</p>
                              {item.is_composite && (
                                <Badge variant="outline" className="text-xs bg-purple-50 text-purple-600 border-purple-200">
                                  Kit
                                </Badge>
                              )}
                            </div>
                            {item.description && (
                              <p className="text-xs text-slate-500 truncate max-w-xs">{item.description}</p>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {item.category && (
                            <Badge variant="outline" className="text-xs">{item.category}</Badge>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <span className={`text-sm font-semibold ${item.is_low_stock ? 'text-orange-600' : 'text-slate-800'}`}>
                              {item.quantity} {item.unit_of_measurement}
                            </span>
                            {item.is_low_stock && (
                              <Badge variant="destructive" className="text-xs">Low</Badge>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-slate-600">${item.unit_price.toFixed(2)}</span>
                        </td>
                        <td className="px-4 py-3">
                          <div>
                            <span className="text-sm text-slate-600 block">{item.warehouse}</span>
                            {item.bin_location && (
                              <span className="text-xs text-slate-400">📍 {item.bin_location}</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {item.tracking_type && item.tracking_type !== 'none' && (
                            <Badge variant="outline" className="text-xs bg-cyan-50 text-cyan-600 border-cyan-200">
                              {item.tracking_type === 'serial' ? '🔢 Serial' : '📦 Batch'}
                            </Badge>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-slate-400 hover:text-red-600"
                            onClick={() => handleDeleteItem(item.id, item.name)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View */}
              <div className="md:hidden divide-y divide-slate-200">
                {filteredItems.map((item) => (
                  <div key={item.id} className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="font-medium text-slate-800">{item.name}</p>
                        <p className="text-xs font-mono text-slate-500">{item.sku}</p>
                      </div>
                      {item.is_low_stock && (
                        <Badge variant="destructive" className="text-xs">Low Stock</Badge>
                      )}
                    </div>
                    <div className="space-y-1 text-sm">
                      <p className="text-slate-600">Quantity: <span className="font-semibold">{item.quantity} {item.unit_of_measurement}</span></p>
                      <p className="text-slate-600">Price: ${item.unit_price.toFixed(2)}</p>
                      <p className="text-slate-600">Warehouse: {item.warehouse}</p>
                    </div>
                  </div>
                ))}
              </div>

              {filteredItems.length === 0 && (
                <div className="text-center py-12 text-slate-400">
                  <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No items found</p>
                </div>
              )}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Add Item Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Inventory Item</DialogTitle>
            <DialogDescription>Create a new item in inventory</DialogDescription>
          </DialogHeader>

          <form onSubmit={handleCreateItem} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>SKU *</Label>
                <Input
                  value={itemFormData.sku}
                  onChange={(e) => setItemFormData({ ...itemFormData, sku: e.target.value })}
                  placeholder="ITEM-001"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Name *</Label>
                <Input
                  value={itemFormData.name}
                  onChange={(e) => setItemFormData({ ...itemFormData, name: e.target.value })}
                  placeholder="Item name"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={itemFormData.description}
                onChange={(e) => setItemFormData({ ...itemFormData, description: e.target.value })}
                placeholder="Item description..."
                className="min-h-[80px]"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Category</Label>
                <Input
                  value={itemFormData.category}
                  onChange={(e) => setItemFormData({ ...itemFormData, category: e.target.value })}
                  placeholder="Electronics, Office, etc."
                />
              </div>
              <div className="space-y-2">
                <Label>Warehouse *</Label>
                <Input
                  value={itemFormData.warehouse}
                  onChange={(e) => setItemFormData({ ...itemFormData, warehouse: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Initial Quantity</Label>
                <Input
                  type="number"
                  value={itemFormData.quantity}
                  onChange={(e) => setItemFormData({ ...itemFormData, quantity: parseInt(e.target.value) || 0 })}
                  min="0"
                />
              </div>
              <div className="space-y-2">
                <Label>Unit Price</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={itemFormData.unit_price}
                  onChange={(e) => setItemFormData({ ...itemFormData, unit_price: parseFloat(e.target.value) || 0 })}
                  min="0"
                />
              </div>
              <div className="space-y-2">
                <Label>Reorder Point</Label>
                <Input
                  type="number"
                  value={itemFormData.reorder_point}
                  onChange={(e) => setItemFormData({ ...itemFormData, reorder_point: parseInt(e.target.value) || 0 })}
                  min="0"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Unit of Measurement</Label>
              <Input
                value={itemFormData.unit_of_measurement}
                onChange={(e) => setItemFormData({ ...itemFormData, unit_of_measurement: e.target.value })}
                placeholder="pcs, kg, liter, box, etc."
              />
            </div>

            {/* Phase 2: Advanced Features */}
            <div className="pt-4 border-t border-slate-200">
              <h4 className="text-sm font-semibold text-slate-700 mb-4">📦 Advanced Tracking (Phase 2)</h4>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Tracking Type</Label>
                  <Select
                    value={itemFormData.tracking_type}
                    onValueChange={(value) => setItemFormData({ ...itemFormData, tracking_type: value })}
                  >
                    <SelectTrigger className="bg-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      <SelectItem value="serial">Serial Number</SelectItem>
                      <SelectItem value="batch">Batch/Lot Number</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500">Track items by serial or batch numbers</p>
                </div>

                <div className="space-y-2">
                  <Label>Bin Location</Label>
                  <Input
                    value={itemFormData.bin_location}
                    onChange={(e) => setItemFormData({ ...itemFormData, bin_location: e.target.value })}
                    placeholder="e.g., A1-B2, Shelf-3"
                  />
                  <p className="text-xs text-slate-500">Warehouse bin/shelf location</p>
                </div>
              </div>

              <div className="flex items-center gap-2 mt-4 p-3 bg-slate-50 rounded-lg border border-slate-200">
                <input
                  type="checkbox"
                  id="is_composite"
                  checked={itemFormData.is_composite}
                  onChange={(e) => setItemFormData({ ...itemFormData, is_composite: e.target.checked })}
                  className="w-4 h-4 text-cyan-600 rounded border-slate-300"
                />
                <div className="flex-1">
                  <Label htmlFor="is_composite" className="text-sm font-medium cursor-pointer">
                    Composite Item (Kit/Bundle)
                  </Label>
                  <p className="text-xs text-slate-500">This item is made from multiple components</p>
                </div>
              </div>

              {itemFormData.is_composite && (
                <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-xs text-amber-800">
                    💡 Composite item management (component selection) will be added in the next update
                  </p>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button type="button" variant="ghost" onClick={() => setShowAddDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-cyan-500 hover:bg-cyan-600">
                <Plus className="w-4 h-4 mr-2" />
                Create Item
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Stock Transaction Dialog */}
      <Dialog open={showTransactionDialog} onOpenChange={setShowTransactionDialog}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle>Stock Transaction</DialogTitle>
            <DialogDescription>Add or remove stock from inventory</DialogDescription>
          </DialogHeader>

          <form onSubmit={handleCreateTransaction} className="space-y-4">
            <div className="space-y-2">
              <Label>Item *</Label>
              <Select
                value={transactionFormData.item_id}
                onValueChange={(value) => setTransactionFormData({ ...transactionFormData, item_id: value })}
                required
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select item" />
                </SelectTrigger>
                <SelectContent>
                  {items.map(item => (
                    <SelectItem key={item.id} value={item.id}>
                      {item.name} ({item.sku}) - Current: {item.quantity}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Type *</Label>
                <Select
                  value={transactionFormData.transaction_type}
                  onValueChange={(value) => setTransactionFormData({ ...transactionFormData, transaction_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="in">
                      <div className="flex items-center gap-2">
                        <ArrowUpCircle className="w-4 h-4 text-green-600" />
                        Stock In
                      </div>
                    </SelectItem>
                    <SelectItem value="out">
                      <div className="flex items-center gap-2">
                        <ArrowDownCircle className="w-4 h-4 text-red-600" />
                        Stock Out
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Quantity *</Label>
                <Input
                  type="number"
                  value={transactionFormData.quantity}
                  onChange={(e) => setTransactionFormData({ ...transactionFormData, quantity: parseInt(e.target.value) || 0 })}
                  min="1"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Reference (Ticket/Work Order)</Label>
              <Input
                value={transactionFormData.reference}
                onChange={(e) => setTransactionFormData({ ...transactionFormData, reference: e.target.value })}
                placeholder="TKT-2024-00001 or WO-123"
              />
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={transactionFormData.notes}
                onChange={(e) => setTransactionFormData({ ...transactionFormData, notes: e.target.value })}
                placeholder="Additional notes..."
                className="min-h-[80px]"
              />
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button type="button" variant="ghost" onClick={() => setShowTransactionDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-cyan-500 hover:bg-cyan-600">
                Create Transaction
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
